/*============================================================================
  HC - IB desktop category menu controller - 2026-09-30

  Drives snippets/ib-category-menu.liquid, one instance per department tab
  that uses it (settings.category_menus_v2). Loaded from layout/theme.liquid
  only while that setting names a tab, after theme.js and
  assets/ib-menu-sheet.js.

  theme.js still opens and closes each menu, and the menu sheet
  (window.IBMenuSheet, shared with the BRANDS menu) follows it: it holds the
  page still while a menu is open, puts ib-cm-open on <html> (which hides the
  product pages' sticky buy bars: assets/ib-product.css,
  assets/product-education.css.liquid), closes the menu with its close
  button, the dimmed page, and when a popup or the cart opens, focus leaves,
  the header compresses or the window narrows past the desktop layout, and
  gives keys and focus back as it should. What this adds:
  - The categories' pictures load on intent: when the pointer or focus
    reaches the tab, or the menu opens. Until then their URLs wait in
    data-ib-cm-src, so a closed menu costs no image downloads.
  - The sheet is measured on opening: never past the bottom of the window
    (the columns scroll inside it when they need more), with the page below
    it dimmed down to the window's bottom. The dim goes after the fade-out.
  - Arrow keys move between the links: Down from the tab goes to the first,
    Up and Down go in reading order, down each column, Left and Right to the
    nearest link in the next column. Each move scrolls the columns to show
    the link.
  - Until it has set a menu up (is-ready on the root), the CSS leaves the
    pictures out: without this script they would never load.

  Exported on window.IBCategoryMenu for the tests. ES5 on purpose (no build
  step).
==============================================================================*/
(function () {
  'use strict';

  var SELECTORS = {
    root: '[data-ib-cm]',
    panel: '[data-ib-cm-panel]',
    scroller: '[data-ib-cm-scroller]',
    close: '[data-ib-cm-close]',
    scrim: '[data-ib-cm-scrim]',
    lazyImg: 'img[data-ib-cm-src]',
    link: '.ib-cm__head, .ib-cm__sub'
  };

  var PAGE_OPEN_CLASS = 'ib-cm-open'; // on <html> while a menu is open

  /* ------------------------------------------------------------------------
     Small DOM utilities
  ------------------------------------------------------------------------ */

  function toArray(list) {
    var out = [];
    if (!list) return out;
    for (var i = 0; i < list.length; i++) out.push(list[i]);
    return out;
  }

  function closest(el, selector) {
    if (!el || el.nodeType !== 1) return null;
    if (typeof el.closest === 'function') return el.closest(selector);
    for (var n = el; n && n.nodeType === 1; n = n.parentNode) {
      if (n.matches && n.matches(selector)) return n;
    }
    return null;
  }

  // Moves a waiting picture's URL into src. 'is-loaded' fades it in; one that
  // fails leaves the empty tile.
  function loadImage(img) {
    var src = img.getAttribute('data-ib-cm-src');
    if (!src) return;
    img.addEventListener('load', function () {
      img.classList.add('is-loaded');
    });
    img.setAttribute('src', src);
    img.removeAttribute('data-ib-cm-src');
  }

  /* ------------------------------------------------------------------------
     Controller
  ------------------------------------------------------------------------ */

  function IBCategoryMenu(root) {
    this.root = root;
    this.panel = root.querySelector(SELECTORS.panel);
    this.scroller = root.querySelector(SELECTORS.scroller);
    this.hydrated = false;

    this.sheet = window.IBMenuSheet.create(this, {
      root: root,
      pageClass: PAGE_OPEN_CLASS,
      scroller: this.scroller,
      // The dimmed page is inside the menu to theme.js, which leaves clicks
      // there alone: it closes here.
      closers: [root.querySelector(SELECTORS.close), root.querySelector(SELECTORS.scrim)],
      links: SELECTORS.link
    });
    this.sheet.sync();
    // The CSS shows the pictures from here on.
    root.classList.add('is-ready');
  }

  IBCategoryMenu.prototype = {
    destroy: function () {
      this.sheet.destroy();
    },

    // Starts the pictures, once. Ten or so small ones, all shown at once.
    hydrate: function () {
      if (this.hydrated) return;
      this.hydrated = true;
      var imgs = toArray(this.root.querySelectorAll(SELECTORS.lazyImg));
      for (var i = 0; i < imgs.length; i++) loadImage(imgs[i]);
    },

    setVar: function (name, value) {
      if (this.root.style && typeof this.root.style.setProperty === 'function') this.root.style.setProperty(name, value);
    },

    /*
      The room under the nav bar varies (the sticky header compresses, the
      announcement bar can be dismissed): the sheet may take it all
      (--ib-cm-avail) and no more, and the dim fills what the sheet leaves,
      down to the bottom of the window (--ib-cm-dim). is-overflowing fades
      the columns' foot when they scroll. On opening and on resize.
    */
    measure: function () {
      var h = window.innerHeight || 0;
      var panel = this.panel || this.root;
      if (!h || typeof panel.getBoundingClientRect !== 'function') return;
      var top = panel.getBoundingClientRect().top;
      this.setVar('--ib-cm-avail', Math.max(0, Math.floor(h - top)) + 'px');
      var bottom = panel.getBoundingClientRect().bottom;
      this.setVar('--ib-cm-dim', Math.max(0, Math.floor(h - bottom)) + 'px');
      var sc = this.scroller;
      this.root.classList.toggle('is-overflowing', Boolean(sc && (sc.scrollHeight || 0) > (sc.clientHeight || 0) + 1));
    },

    // After the fade-out: a closed menu adds no height to the page.
    reset: function () {
      this.setVar('--ib-cm-dim', '0px');
    },

    /* ---------------- keyboard ---------------- */

    // Every link in the sheet, in reading order: down each column.
    links: function () {
      return this.scroller ? toArray(this.scroller.querySelectorAll(SELECTORS.link)) : [];
    },

    /*
      Up and Down go to the link before or after in reading order (Up from
      the first goes back to the tab). Left and Right go to the nearest link
      in the column beside, level with this one.
    */
    moveFrom: function (from, key) {
      var links = this.links();
      var at = links.indexOf(from);
      if (at === -1) return;
      var next = null;
      if (key === 'ArrowDown') next = links[at + 1] || null;
      else if (key === 'ArrowUp') next = at > 0 ? links[at - 1] : this.sheet.trigger;
      else {
        var right = key === 'ArrowRight';
        var r = from.getBoundingClientRect();
        var cy = r.top + r.height / 2;
        var bestDx = null;
        var bestDy = null;
        for (var i = 0; i < links.length; i++) {
          var o = links[i].getBoundingClientRect();
          var dx = right ? o.left - r.left : r.left - o.left;
          if (dx <= 8) continue;
          var dy = Math.abs(o.top + o.height / 2 - cy);
          // The nearest column first, then the link in it most level with this one.
          if (bestDx === null || dx < bestDx - 8 || (Math.abs(dx - bestDx) <= 8 && dy < bestDy)) {
            bestDx = dx;
            bestDy = dy;
            next = links[i];
          }
        }
      }
      this.sheet.reveal(next);
    },

    // A key the sheet hands over: pressed in the open menu or on the page.
    keydown: function (evt, key) {
      var target = evt.target;
      // Down from the open tab goes into the menu, to the first link, shown
      // wherever the columns were scrolled to. Up there does nothing.
      if (target === this.sheet.trigger && (key === 'ArrowDown' || key === 'ArrowUp')) {
        var first = key === 'ArrowDown' ? this.links()[0] : null;
        if (first) {
          evt.preventDefault();
          this.sheet.reveal(first);
        }
        return;
      }
      if (/^Arrow/.test(key) && closest(target, SELECTORS.link) && this.root.contains(target)) {
        evt.preventDefault();
        this.moveFrom(target, key);
        return;
      }
      this.sheet.scrollKey(evt, key);
    }
  };

  /* ------------------------------------------------------------------------
     Public surface + boot
  ------------------------------------------------------------------------ */

  var api = {
    instances: []
  };

  window.IBCategoryMenu = api;

  // Without the sheet (blocked, or failed) the menus stay stock mega menus,
  // their pictures left out by the CSS.
  function boot() {
    for (var i = 0; i < api.instances.length; i++) api.instances[i].destroy();
    api.instances = [];
    if (!window.IBMenuSheet) return;
    var roots = toArray(document.querySelectorAll(SELECTORS.root));
    for (var r = 0; r < roots.length; r++) api.instances.push(new IBCategoryMenu(roots[r]));
  }

  boot();

  /*
    Theme editor: the header re-renders on every setting change. Listened for
    once, here, not by an instance: boot() destroys the instances and all
    their listeners. Boots again when the reloaded section has a menu, or when
    a running instance's menu is gone.
  */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (evt) {
      var section = evt && evt.target;
      var fresh = section && typeof section.querySelector === 'function' && section.querySelector(SELECTORS.root);
      var stale = false;
      for (var i = 0; i < api.instances.length; i++) {
        if (!document.contains(api.instances[i].root)) stale = true;
      }
      if (fresh || stale) boot();
    });
  }
})();
